import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash.isequal';
import bowser from 'yola-bowser';
import { customization, dialogs, i18next } from '@yola/ws-sdk';
import { HSLColor } from '@yola/yola-palette-generator';
import segment from 'src/js/modules/analytics/segment';
import dialogTypes from '../../dialogs/constants/dialog-types';
import useSharedDataSnapshot from '../../dialogs/hooks/use-shared-data-snapshot';
import ColorSettingsDialog from '../components/color-settings-dialog';
import DEFAULT_COLOR_SCHEMES from '../constants/color-schemes';
import constants from '../constants';
import helpers from '../helpers';
import actions from '../actions';
import getGradientTraitTypeByGradientModelType from '../helpers/get-gradient-trait-type-by-gradient-model-type';
import useColorModeState from '../hooks/use-color-mode-state';
import usePredefinedGradients from '../hooks/use-predefined-gradients';
import supportedColorModes from '../constants/supported-color-modes';
import variableNames from '../constants/variable-names';

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const {
  buildGradientModelByString,
  getAdvancedColorsToOverride,
  getResolvedColorAlphaValue,
  parseHslColorString,
  stringifyGradientModel,
  updateActiveColorStopInGradientModel,
  updateLivePaletteElement,
} = helpers;

const getColorSettingsCaptions = () => ({
  title: i18next.t('Color settings'),
  apply: i18next.t('Save'),
  cancel: i18next.t('Cancel'),
  opacity: i18next.t('Opacity'),
  gradientPicker: {
    colorStopControlTitle: i18next.t('Stop color'),
    colorStopControlPopoverContent: i18next.t(
      'Color at this gradient stop. Move it along the slider to adjust the blend.'
    ),
    linearOptionLabel: i18next.t('Linear'),
    radialOptionLabel: i18next.t('Radial'),
    flipActionLabel: i18next.t('Flip order'),
    deleteActionLabel: i18next.t('Delete'),
  },
});

const GRADIENT_CHANGE_REASONS = {
  CHANGE_LINEAR_ANGLE: 'change-linear-angle',
  CHANGE_RADIAL_POSITION: 'change-radial-position',
  CHANGE_STOP_POSITION: 'change-stop-position',
  ADD_STOP: 'add-stop',
  FLIP_STOPS: 'flip-stops',
  DELETE_STOP: 'delete-stop',
};

function AdvancedColorPickerContainer({
  // eslint-disable-next-line no-unused-vars
  triggerId = null,
  variableName,
  colorSchemeId,
  color: initialColor,
  backgroundColor,
  schemeVariables,
  opacity = '',
  getSharedData,
  resolveSharedData,
  onDialogMainAction,
  onDialogCancel,
  scrollTop = null,
  headerTextContentVisibility = null,
  variableId,
  variableCategory,
  supportedModes = [supportedColorModes.SOLID],
  gradient = null,
  isOverridden = false,
}) {
  const sharedDataSnapshot = useSharedDataSnapshot(getSharedData);

  const { offsetX, offsetY, originPalette, editingButtonPresetId, engine } = sharedDataSnapshot;

  const dispatch = useDispatch();

  const previewFrameRef = useRef(null);
  const latestPreviewRef = useRef(null);

  const [dialogPosition, setDialogPosition] = useState({ offsetX, offsetY });
  const [gradientPickerKey, setGradientPickerKey] = useState(0);

  const {
    colorMode,
    initialGradientColorModel,
    initialSolidColorHSLA,
    solidColorHSLA,
    updateSolidColorHSLA,
    gradientColorModel,
    updateGradientColorModel,
    setGradientBaseColorTypeToAuto,
    handleColorModeChange,
    buildSharedDataPatch,
    removeInternalSharedData,
    gradientBaseColorHSLA,
    getGradientBaseColorType,
  } = useColorModeState({
    sharedData: sharedDataSnapshot,
    initialColor,
    opacity,
    gradientCSSValue: gradient,
  });
  const { predefinedGradients, saveCustomGradient } = usePredefinedGradients();

  const customColors = useSelector(customization.selectors.getCustomColors);
  const buttonPreset = useSelector((state) =>
    editingButtonPresetId
      ? customization.selectors.getButtonPreset(state, editingButtonPresetId)
      : customization.accessors.getCurrentButtonPreset(state)
  );

  const captions = useMemo(() => getColorSettingsCaptions(), []);

  const predefinedColors = useMemo(() => {
    const originColors = DEFAULT_COLOR_SCHEMES.map((color) => ({
      value: originPalette.colors[color],
      opacity: constants.MAX_OPACITY_VALUE,
    }));

    const seen = new Set();
    const result = [];

    [...originColors, ...customColors].forEach((color) => {
      if (!opacity && color.opacity !== constants.MAX_OPACITY_VALUE) return;

      const key = `${color.value}|${color.opacity}`;
      if (seen.has(key)) return;

      seen.add(key);
      result.push(color);
    });

    return result;
  }, [customColors, opacity, originPalette]);

  const gradientBaseColorCSSValue = useMemo(
    () =>
      gradientBaseColorHSLA
        ? new HSLColor(...Object.values(gradientBaseColorHSLA)).toCSSValue()
        : null,
    [gradientBaseColorHSLA]
  );

  const applyCompiledPalette = useCallback(
    (compiledPalette) => {
      updateLivePaletteElement(compiledPalette);

      if (variableName === variableNames.BLOCK_BACKGROUND) {
        dispatch(actions.changeColor(compiledPalette.variables));
      }
    },
    [dispatch, variableName]
  );

  const buildNextPalette = useCallback(
    ({ nextSolidColorHSLA, nextGradientModel }) => {
      const overrides = getAdvancedColorsToOverride({
        hsla: nextSolidColorHSLA,
        variableName,
        backgroundColor,
        variables: schemeVariables,
        buttonPreset,
        gradientModel: nextGradientModel,
      });

      return engine.updateSchemeOverrides(colorSchemeId, overrides);
    },
    [backgroundColor, buttonPreset, colorSchemeId, engine, schemeVariables, variableName]
  );

  const cancelScheduledPreview = useCallback(() => {
    if (!previewFrameRef.current) return;

    cancelAnimationFrame(previewFrameRef.current);
    previewFrameRef.current = null;
  }, []);

  const scheduleLivePreview = useCallback(
    ({ nextSolidColorHSLA, nextGradientModel }) => {
      if (bowser.mobile) {
        return;
      }

      latestPreviewRef.current = {
        nextSolidColorHSLA,
        nextGradientModel,
      };

      if (previewFrameRef.current) {
        return;
      }

      previewFrameRef.current = requestAnimationFrame(() => {
        previewFrameRef.current = null;

        const compiledPalette = buildNextPalette({ nextSolidColorHSLA, nextGradientModel });

        updateLivePaletteElement(compiledPalette);
      });
    },
    [buildNextPalette]
  );

  const onClose = () => {
    resolveSharedData((currentSharedData) => ({
      ...removeInternalSharedData(currentSharedData),
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
    }));

    dispatch(
      dialogs.actions.show(dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG, {
        colorSchemeId,
        scrollTop,
        headerTextContentVisibility,
      })
    );
  };

  const updateSharedData = useCallback(
    (customData) => {
      resolveSharedData({
        offsetX: dialogPosition.offsetX,
        offsetY: dialogPosition.offsetY,
        ...customData,
      });
    },
    [dialogPosition, resolveSharedData]
  );

  const getSolidColorCompiledPalette = () => {
    const isAdjusted = !isEqual(solidColorHSLA, initialSolidColorHSLA);

    return isAdjusted
      ? buildNextPalette({
          nextSolidColorHSLA: solidColorHSLA,
          nextGradientModel: null,
        })
      : null;
  };

  const getGradientColorCompiledPalette = () => {
    if (!gradientColorModel) {
      return null;
    }

    const nextGradientModel = gradientColorModel;
    const nextGradientCSSValue = stringifyGradientModel(nextGradientModel) || 'none';
    const initialGradientCSSValue = stringifyGradientModel(initialGradientColorModel) || 'none';
    const isGradientAdjusted = nextGradientCSSValue !== initialGradientCSSValue;
    const isBaseColorAdjusted = !isEqual(gradientBaseColorHSLA, initialSolidColorHSLA);
    const isAdjusted = isGradientAdjusted || isBaseColorAdjusted;

    return isAdjusted
      ? buildNextPalette({
          nextSolidColorHSLA: gradientBaseColorHSLA,
          nextGradientModel,
        })
      : null;
  };

  const handleSubmit = () => {
    cancelScheduledPreview();

    const compiledPalette =
      colorMode === supportedColorModes.SOLID
        ? getSolidColorCompiledPalette()
        : getGradientColorCompiledPalette();
    const isAdjusted = compiledPalette !== null;

    if (isAdjusted && compiledPalette) {
      applyCompiledPalette(compiledPalette);
    }

    let additionalTraits;

    if (colorMode === supportedColorModes.SOLID) {
      const hslColorString = new HSLColor(...Object.values(solidColorHSLA)).toCSSValue(false);
      const isSelectedColorPredefined = predefinedColors.some(
        ({ value, opacity: predefinedOpacity }) =>
          value === hslColorString && predefinedOpacity === solidColorHSLA.a
      );

      if (!isSelectedColorPredefined) {
        dispatch(
          customization.actions.addCustomColor({
            value: hslColorString,
            opacity: solidColorHSLA.a || constants.MAX_OPACITY_VALUE,
          })
        );
      }

      additionalTraits = {
        colorType: supportedColorModes.SOLID,
        gradientType: 'none',
        baseColor: 'none',
      };
    }

    if (colorMode === supportedColorModes.GRADIENT) {
      const gradientCSSValue = stringifyGradientModel(gradientColorModel) || null;

      saveCustomGradient(gradientCSSValue);

      additionalTraits = {
        colorType: supportedColorModes.GRADIENT,
        gradientType: getGradientTraitTypeByGradientModelType(gradientColorModel?.type),
        baseColor: getGradientBaseColorType(),
      };
    }

    onDialogMainAction({
      colorAdjusted: isAdjusted,
      variableId,
      variableCategory,
      ...additionalTraits,
    });
    onClose();
  };

  const handleCancel = () => {
    cancelScheduledPreview();

    const restoredPalette = isOverridden
      ? buildNextPalette({
          nextSolidColorHSLA: initialSolidColorHSLA,
          nextGradientModel: initialGradientColorModel,
        })
      : engine.resetSchemeOverrides(colorSchemeId, [variableName]);
    applyCompiledPalette(restoredPalette);

    onDialogCancel();
    onClose();
  };

  const applySolidColor = ({ hsl }) => {
    updateSolidColorHSLA({ ...hsl });

    scheduleLivePreview({
      nextSolidColorHSLA: hsl,
      nextGradientModel: null,
    });
  };

  const handlePredefinedColorClick = ({ hslString, opacity: alpha }) => {
    const { h, s, l } = parseHslColorString(hslString);
    applySolidColor({ hsl: { h, s, l, a: getResolvedColorAlphaValue(alpha) } });
  };

  const applyGradientColorModel = useCallback(
    ({ nextGradientModel, nextSolidColorHSLA = solidColorHSLA }) => {
      updateGradientColorModel(nextGradientModel);

      scheduleLivePreview({
        nextSolidColorHSLA,
        nextGradientModel,
      });
    },
    [solidColorHSLA, scheduleLivePreview, updateGradientColorModel]
  );

  const handleGradientChange = useCallback(
    (change) => {
      const nextGradientModel = change.model;

      applyGradientColorModel({ nextGradientModel });
    },
    [applyGradientColorModel]
  );

  const handleGradientChangeEnd = useCallback(
    (change) => {
      const nextGradientModel = change.model;

      applyGradientColorModel({ nextGradientModel });

      switch (change.reason) {
        case GRADIENT_CHANGE_REASONS.CHANGE_LINEAR_ANGLE: {
          trackEvent(events.DESIGN_SETTINGS_GRADIENT_ANGLE_CHANGED, {
            angle: Number(nextGradientModel.orientation.value),
          });
          break;
        }
        case GRADIENT_CHANGE_REASONS.CHANGE_RADIAL_POSITION:
        case GRADIENT_CHANGE_REASONS.CHANGE_STOP_POSITION: {
          trackEvent(events.DESIGN_SETTINGS_GRADIENT_POSITION_CHANGED);
          break;
        }
        case GRADIENT_CHANGE_REASONS.ADD_STOP:
        case GRADIENT_CHANGE_REASONS.FLIP_STOPS:
        case GRADIENT_CHANGE_REASONS.DELETE_STOP: {
          trackEvent(events.DESIGN_SETTINGS_GRADIENT_CONTROL_CLICKED, {
            gradientType: getGradientTraitTypeByGradientModelType(nextGradientModel?.type),
            controlType: change.reason,
            stopsCount: nextGradientModel?.colorStops?.length,
          });
          break;
        }
        default:
          break;
      }
    },
    [applyGradientColorModel]
  );

  const handlePredefinedGradientClick = useCallback(
    ({ value }) => {
      const nextGradientModel = buildGradientModelByString(value);

      if (!nextGradientModel) {
        return;
      }

      setGradientBaseColorTypeToAuto();
      setGradientPickerKey((currentKey) => currentKey + 1);
      applyGradientColorModel({ nextGradientModel });
    },
    [applyGradientColorModel, setGradientBaseColorTypeToAuto]
  );

  const handleGradientColorStopClick = useCallback(
    ({ colorStopId, colorStop }) => {
      const [h, s, l, a] = colorStop.value.map(Number);
      const hslColor = new HSLColor(h, s, l, a);

      const nextSharedGradientModel = gradientColorModel
        ? {
            ...gradientColorModel,
            activeColorStopId: colorStopId,
            colorStops: gradientColorModel.colorStops.map((gradientColorStop) =>
              gradientColorStop.id === colorStopId ? colorStop : gradientColorStop
            ),
          }
        : null;

      applyGradientColorModel({ nextGradientModel: nextSharedGradientModel });
      updateSharedData(
        buildSharedDataPatch({
          gradientColorModel: nextSharedGradientModel,
        })
      );

      const handleColorStopPickerChange = ({ hsl }) => {
        if (!nextSharedGradientModel) return;

        const nextGradientModel = updateActiveColorStopInGradientModel(
          nextSharedGradientModel,
          hsl
        );

        applyGradientColorModel({ nextGradientModel, nextSolidColorHSLA: hsl });
      };

      const handleColorStopPickerSubmit = ({
        color: hslaColor,
        offsetX: nextOffsetX,
        offsetY: nextOffsetY,
      }) => {
        const nextGradientModel = updateActiveColorStopInGradientModel(
          nextSharedGradientModel,
          hslaColor
        );

        resolveSharedData({
          ...buildSharedDataPatch({ gradientColorModel: nextGradientModel }),
          offsetX: nextOffsetX,
          offsetY: nextOffsetY,
        });
      };

      const handleColorStopPickerCancel = ({ offsetX: nextOffsetX, offsetY: nextOffsetY }) => {
        cancelScheduledPreview();

        const restoredPalette = buildNextPalette({
          nextSolidColorHSLA: solidColorHSLA,
          nextGradientModel: nextSharedGradientModel,
        });

        applyCompiledPalette(restoredPalette);
        resolveSharedData({ offsetX: nextOffsetX, offsetY: nextOffsetY });
      };

      dispatch(
        dialogs.actions.show(dialogTypes.COLOR_PICKER_DIALOG, {
          color: hslColor.toCSSValue(),
          enableAlpha: true,
          callerDialogType: dialogTypes.ADVANCED_COLOR_PICKER_DIALOG,
          supportedModes: [supportedColorModes.SOLID],
          mode: supportedColorModes.SOLID,
          onColorChange: handleColorStopPickerChange,
          onDialogMainAction: handleColorStopPickerSubmit,
          onDialogCancel: handleColorStopPickerCancel,
          callerDialogProps: {
            color: new HSLColor(...Object.values(solidColorHSLA)).toCSSValue(),
            variableName,
            backgroundColor,
            schemeVariables,
            colorSchemeId,
            opacity,
            scrollTop,
            headerTextContentVisibility,
            variableId,
            variableCategory,
            supportedModes,
            gradient,
          },
        })
      );
    },
    [
      gradientColorModel,
      applyGradientColorModel,
      updateSharedData,
      buildSharedDataPatch,
      resolveSharedData,
      dispatch,
      solidColorHSLA,
      buildNextPalette,
      cancelScheduledPreview,
      applyCompiledPalette,
      variableName,
      backgroundColor,
      schemeVariables,
      colorSchemeId,
      opacity,
      scrollTop,
      headerTextContentVisibility,
      variableId,
      variableCategory,
      supportedModes,
      gradient,
    ]
  );

  const handleGradientBaseColorClick = useCallback(() => {
    updateSharedData(buildSharedDataPatch());

    const handleFallbackPickerSubmit = ({ color, offsetX: nextOffsetX, offsetY: nextOffsetY }) => {
      resolveSharedData({
        ...buildSharedDataPatch({ gradientCustomBaseColorHSLA: color }),
        offsetX: nextOffsetX,
        offsetY: nextOffsetY,
      });
    };

    const handleFallbackPickerCancel = ({ offsetX: nextOffsetX, offsetY: nextOffsetY }) => {
      cancelScheduledPreview();

      const restoredPalette = buildNextPalette({
        nextSolidColorHSLA: gradientBaseColorHSLA,
        nextGradientModel: gradientColorModel,
      });

      applyCompiledPalette(restoredPalette);
      resolveSharedData({ offsetX: nextOffsetX, offsetY: nextOffsetY });
    };

    dispatch(
      dialogs.actions.show(dialogTypes.COLOR_PICKER_DIALOG, {
        color: gradientBaseColorCSSValue,
        enableAlpha: false,
        callerDialogType: dialogTypes.ADVANCED_COLOR_PICKER_DIALOG,
        supportedModes: [supportedColorModes.SOLID],
        mode: supportedColorModes.SOLID,
        onDialogMainAction: handleFallbackPickerSubmit,
        onDialogCancel: handleFallbackPickerCancel,
        callerDialogProps: {
          color: new HSLColor(...Object.values(solidColorHSLA)).toCSSValue(),
          variableName,
          backgroundColor,
          schemeVariables,
          colorSchemeId,
          opacity,
          scrollTop,
          headerTextContentVisibility,
          variableId,
          variableCategory,
          triggerId,
          supportedModes,
          gradient,
        },
      })
    );
  }, [
    updateSharedData,
    buildSharedDataPatch,
    resolveSharedData,
    dispatch,
    gradientBaseColorCSSValue,
    solidColorHSLA,
    gradientColorModel,
    gradientBaseColorHSLA,
    buildNextPalette,
    cancelScheduledPreview,
    applyCompiledPalette,
    variableName,
    backgroundColor,
    schemeVariables,
    colorSchemeId,
    opacity,
    scrollTop,
    headerTextContentVisibility,
    variableId,
    variableCategory,
    triggerId,
    supportedModes,
    gradient,
  ]);

  const handleModeChange = useCallback(
    (nextMode) => {
      if (nextMode === colorMode) {
        return;
      }

      const nextState = handleColorModeChange(nextMode);
      scheduleLivePreview(nextState);
      trackEvent(events.DESIGN_SETTINGS_COLOR_TYPE_SWITCHED, { colorType: nextMode });
    },
    [colorMode, handleColorModeChange, scheduleLivePreview]
  );

  const handleDragEnd = (_, { x, y }) => {
    setDialogPosition({
      offsetX: x,
      offsetY: y,
    });
  };

  useEffect(() => {
    if (!triggerId) return;

    const gradientAvailable = supportedModes.includes(supportedColorModes.GRADIENT);

    trackEvent(events.DESIGN_SETTINGS_COLOR_SETTINGS_DISPLAYED, {
      dialogId: dialogTypes.ADVANCED_COLOR_PICKER_DIALOG,
      variableId,
      variableCategory,
      triggerId,
      colorType: colorMode,
      gradientAvailable,
    });

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      cancelScheduledPreview();
    },
    [cancelScheduledPreview]
  );

  return (
    <ColorSettingsDialog
      captions={captions}
      color={solidColorHSLA}
      enableAlpha={Boolean(opacity)}
      predefinedColors={predefinedColors}
      predefinedGradients={predefinedGradients}
      gradientBaseColor={gradientBaseColorCSSValue}
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      supportedModes={supportedModes}
      onColorChange={applySolidColor}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onDragEnd={handleDragEnd}
      onPredefinedColorClick={handlePredefinedColorClick}
      gradientModel={gradientColorModel}
      gradientPickerKey={gradientPickerKey}
      mode={colorMode}
      onModeChange={handleModeChange}
      onGradientChange={handleGradientChange}
      onGradientChangeEnd={handleGradientChangeEnd}
      onGradientColorStopClick={handleGradientColorStopClick}
      onPredefinedGradientClick={handlePredefinedGradientClick}
      onGradientBaseColorClick={handleGradientBaseColorClick}
    />
  );
}

AdvancedColorPickerContainer.propTypes = {
  triggerId: PropTypes.string,
  variableName: PropTypes.string.isRequired,
  colorSchemeId: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string.isRequired,
  schemeVariables: PropTypes.object.isRequired,
  opacity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  scrollTop: PropTypes.number,
  headerTextContentVisibility: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
  variableId: PropTypes.string.isRequired,
  variableCategory: PropTypes.string.isRequired,
  gradient: PropTypes.string,
  supportedModes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(supportedColorModes))),
  isOverridden: PropTypes.bool,
};

export default AdvancedColorPickerContainer;
